import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@cotera/client/v0/components/ui/collapsible';
import { cn } from '@cotera/client/v0/lib/utils';
import type { ToolUIPart, DynamicToolUIPart } from 'ai';
import {
  CheckIcon,
  ChevronDownIcon,
  CircleIcon,
  ClockIcon,
  LoaderIcon,
  WrenchIcon,
  XCircleIcon,
} from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { isValidElement } from 'react';
import { CodeBlock, CodeBlockCopyButton } from './code-block';
import {
  useAnalytics,
  useCocoConversationId,
  useChatSurface,
  CocoEvent,
} from '@cotera/client/app/etc';
import { RunningToolTitle } from '@cotera/client/v0/chat/running-tool-title';

export type ToolProps = ComponentProps<typeof Collapsible>;

export const Tool = ({ className, ...props }: ToolProps) => (
  <Collapsible
    className={cn('px-4 not-prose mb-4 w-full', className)}
    {...props}
  />
);

export type ToolHeaderProps = {
  title?: string;
  type: ToolUIPart['type'] | DynamicToolUIPart['type'];
  state: ToolUIPart['state'] | DynamicToolUIPart['state'];
  className?: string;
  icon?: ReactNode;
  /**
   * Optional pill rendered next to the title. Used by grouped tool rows to make
   * the run count ("5 runs") obvious instead of a subtle "(5)" suffix.
   */
  badge?: ReactNode;
};

const getStatusIcon = (
  status: ToolUIPart['state'] | DynamicToolUIPart['state']
): ReactNode => {
  const icons: Record<
    ToolUIPart['state'] | DynamicToolUIPart['state'],
    ReactNode
  > = {
    'input-streaming': (
      <LoaderIcon className="h-4 w-4 animate-spin text-muted-foreground" />
    ),
    'input-available': (
      <LoaderIcon className="h-4 w-4 animate-spin text-muted-foreground" />
    ),
    //@ts-ignore
    'approval-requested': <ClockIcon className="h-4 w-4 text-yellow-600" />,
    'approval-responded': <CheckIcon className="h-4 w-4 text-blue-600" />,
    'output-available': <CheckIcon className="h-4 w-4 text-success" />,
    'output-error': <XCircleIcon className="h-4 w-4 text-red-600" />,
    'output-denied': <XCircleIcon className="h-4 w-4 text-orange-600" />,
  };

  return (
    icons[status] ?? <CircleIcon className="h-3 w-3 text-muted-foreground" />
  );
};

const isToolHeaderRunningState = (
  state: ToolUIPart['state'] | DynamicToolUIPart['state']
): boolean => state === 'input-streaming' || state === 'input-available';

export const ToolHeader = ({
  className,
  title,
  type,
  state,
  icon,
  badge,
  ...props
}: ToolHeaderProps) => {
  const label = title ?? type.split('-').slice(1).join('-');
  const showShimmer = isToolHeaderRunningState(state);
  return (
    <CollapsibleTrigger
      className={cn(
        'flex w-full items-center justify-between gap-4 py-1',
        className
      )}
      {...props}
    >
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        {icon ?? <WrenchIcon className="h-4 w-4 text-muted-foreground" />}
        {getStatusIcon(state)}
        {showShimmer ? (
          <RunningToolTitle className="italic text-sm">
            {label}
          </RunningToolTitle>
        ) : (
          <span className="italic">{label}</span>
        )}
        {badge !== undefined && badge !== null ? (
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium not-italic leading-none text-primary">
            {badge}
          </span>
        ) : null}
      </div>
      <ChevronDownIcon className="size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
    </CollapsibleTrigger>
  );
};

export type ToolContentProps = ComponentProps<typeof CollapsibleContent>;

export const ToolContent = ({ className, ...props }: ToolContentProps) => (
  <CollapsibleContent
    className={cn(
      'data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 text-popover-foreground outline-none data-[state=closed]:animate-out data-[state=open]:animate-in',
      className
    )}
    {...props}
  />
);

export type ToolInputProps = ComponentProps<'div'> & {
  input: ToolUIPart['input'];
};

export const ToolInput = ({ className, input, ...props }: ToolInputProps) => {
  const analytics = useAnalytics();
  const cocoConversationId = useCocoConversationId();
  const surface = useChatSurface() ?? 'scratch';

  return (
    <div className={cn('space-y-2 overflow-hidden p-4', className)} {...props}>
      <h4 className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
        Parameters
      </h4>
      <div className="rounded-md bg-muted/50">
        <CodeBlock code={JSON.stringify(input, null, 2)} language="json">
          <CodeBlockCopyButton
            onCopy={() => {
              if (cocoConversationId !== null) {
                analytics.track(CocoEvent.CodeBlockCopied, {
                  conversationId: cocoConversationId,
                  surface,
                  language: 'json',
                  source: 'tool-input',
                });
              }
            }}
          />
        </CodeBlock>
      </div>
    </div>
  );
};

export type ToolOutputProps = ComponentProps<'div'> & {
  output: ToolUIPart['output'];
  errorText: ToolUIPart['errorText'];
};

export const ToolOutput = ({
  className,
  output,
  errorText,
  ...props
}: ToolOutputProps) => {
  if (!(output || errorText)) {
    return null;
  }

  let Output = <div>{output as ReactNode}</div>;

  if (typeof output === 'object' && !isValidElement(output)) {
    Output = (
      <CodeBlock code={JSON.stringify(output, null, 2)} language="json" />
    );
  } else if (typeof output === 'string') {
    Output = <CodeBlock code={output} language="json" />;
  }

  return (
    <div className={cn('space-y-2 p-4', className)} {...props}>
      <h4 className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
        {errorText ? 'Error' : 'Result'}
      </h4>
      <div
        className={cn(
          'overflow-x-auto rounded-md text-xs [&_table]:w-full',
          errorText
            ? 'bg-destructive/10 text-destructive'
            : 'bg-muted/50 text-foreground'
        )}
      >
        {errorText && (
          <div className="whitespace-pre-wrap break-words p-3">{errorText}</div>
        )}
        {Output}
      </div>
    </div>
  );
};
